import { useMemo } from 'react';
import { buildMerchantExportFilters } from 'utils/CommonFunction';

/**
 * Derives everything the merchant list needs from its URL filter state:
 *
 *  - `exportFilters` - the same filter set the list query uses, so an export
 *    always matches what is on screen (RAT-1865, extended for labels in
 *    RAT-1690). Depends on the individual keys rather than the params object, so
 *    paging or a page-size change never rebuilds it.
 *  - `selectedLabelIds` - the label filter as an array. The URL carries a
 *    comma-separated string so it round-trips like every other filter; the
 *    picker works in arrays.
 *
 * `withLabels` is false for the roles labels do not exist for: the label filter
 * is then dropped from the export payload and read as empty, so a labelIds left
 * over in the URL cannot narrow their list or their export.
 *
 * Lives in a hook because MerchantList.jsx is at the 1500-line lint ceiling.
 */
const useMerchantListFilters = (params, search, withLabels = true) => {
  const {
    inMaintenance,
    isTemporarilyClosed,
    isComingSoon,
    legalInfoStatus,
    archivedView,
  } = params;
  const labelIds = withLabels ? params.labelIds : '';

  const exportFilters = useMemo(
    () =>
      buildMerchantExportFilters(
        {
          inMaintenance,
          isTemporarilyClosed,
          isComingSoon,
          legalInfoStatus,
          archivedView,
          labelIds,
        },
        search,
      ),
    [
      search,
      inMaintenance,
      isTemporarilyClosed,
      isComingSoon,
      legalInfoStatus,
      archivedView,
      labelIds,
    ],
  );

  const selectedLabelIds = useMemo(
    () => (labelIds ? labelIds.split(',').filter(Boolean) : []),
    [labelIds],
  );

  return { exportFilters, selectedLabelIds };
};

export default useMerchantListFilters;
